import React from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { ImportWizard } from '@/components/leads/import/ImportWizard';
import { ImportHistory } from '@/components/leads/import/ImportHistory';
import { useLeadImport } from '@/hooks/useLeadImport';
import { motion } from 'framer-motion';
import { ArrowLeft, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export const LeadImportPage: React.FC = () => {
  const navigate = useNavigate();
  const { importJobs, getJobErrors } = useLeadImport();

  const handleComplete = () => {
    // Redirect to pipeline page after successful import
    navigate('/pipeline');
  };

  const handleCancel = () => {
    // Go back to pipeline page
    navigate('/pipeline');
  };

  const handleViewErrors = (jobId: string) => {
    // Could open a modal or navigate to error details
    console.log('View errors for job:', jobId);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/pipeline')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Pipeline</span>
            </Button>
          </div>
        </div>

        {/* Page title */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Upload className="w-8 h-8 mr-3 text-blue-600" />
            Import Leads
          </h1>
          <p className="text-gray-600 mt-1">
            Upload a CSV or Excel file to bulk import leads into your CRM
          </p>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Import wizard */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <ImportWizard
                onComplete={handleComplete}
                onCancel={handleCancel}
              />
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Instructions */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Import Instructions</h3>
              <div className="space-y-4 text-sm text-gray-600">
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">1. Prepare your file</h4>
                  <p>Ensure your CSV or Excel file has headers in the first row. Required fields are Lead Name and Company.</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">2. Upload and map</h4>
                  <p>Upload your file and map each column to the corresponding lead field.</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">3. Configure settings</h4>
                  <p>Choose how to handle duplicates and set default values for missing fields.</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">4. Review and import</h4>
                  <p>Start the import and monitor progress. You can download error reports if needed.</p>
                </div>
              </div>
            </Card>

            {/* File format help */}
            <Card className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Supported Formats</h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">CSV (.csv)</span>
                  <span className="text-green-600 font-medium">Recommended</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Excel (.xlsx)</span>
                  <span className="text-gray-500">Supported</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Excel 97-2003 (.xls)</span>
                  <span className="text-gray-500">Supported</span>
                </div>
                <div className="pt-2 border-t">
                  <span className="text-gray-500">Max file size: 10MB</span>
                </div>
              </div>
            </Card>

            {/* Import history */}
            {importJobs.length > 0 && (
              <Card className="p-6">
                <ImportHistory
                  jobs={importJobs.slice(0, 5)}
                  onViewErrors={handleViewErrors}
                />
              </Card>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};
